import React, { useState, useEffect } from 'react';
// import { useTranslation } from 'react-i18next';

interface PaymentGateway {
  id: string;
  name: string;
  priority: number;
  features: {
    upi_qr: boolean;
    payment_links: boolean;
    webhooks: boolean;
    refunds: boolean;
    test_mode: boolean;
  };
}

interface PaymentGatewaySelectorProps {
  onGatewaySelect: (gatewayId: string) => void;
  selectedGateway?: string;
  disabled?: boolean;
}

const PaymentGatewaySelector: React.FC<PaymentGatewaySelectorProps> = ({
  onGatewaySelect,
  selectedGateway,
  disabled = false
}) => {
  // const { t } = useTranslation();
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchActiveGateways();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActiveGateways = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment-gateway/active`);
      const data = await response.json();
      
      if (data.success) {
        setGateways(data.data.gatewaySelection || []);
        // Auto-select the first gateway if none is selected
        if (!selectedGateway && data.data.gatewaySelection.length > 0) {
          onGatewaySelect(data.data.gatewaySelection[0].id);
        }
      } else {
        setError(data.error || 'Failed to fetch payment gateways');
      }
    } catch (error) {
      setError('Failed to fetch payment gateways');
    } finally {
      setLoading(false);
    }
  };

  const getGatewayIcon = (gatewayId: string) => {
    switch (gatewayId) {
      case 'razorpay':
        return '💳';
      case 'sahkar_pg':
        return '🏦';
      default:
        return '💳';
    }
  };

  const getGatewayDescription = (gateway: PaymentGateway) => {
    const features = [];
    if (gateway.features.upi_qr) features.push('UPI QR');
    if (gateway.features.payment_links) features.push('Payment Links');
    if (gateway.features.webhooks) features.push('Webhooks');
    if (gateway.features.refunds) features.push('Refunds');
    
    return features.length > 0 ? features.join(', ') : 'Standard payment processing';
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Payment Gateway
        </label>
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-gray-500">Loading payment gateways...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Payment Gateway
        </label>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={fetchActiveGateways}
            className="mt-2 text-sm text-red-700 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (gateways.length === 0) {
    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Payment Gateway
        </label>
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-600">No payment gateways are currently available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Payment Gateway
      </label>
      <div className="space-y-2">
        {gateways.map((gateway) => (
          <div
            key={gateway.id}
            className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
              selectedGateway === gateway.id
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !disabled && onGatewaySelect(gateway.id)}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <span className="text-2xl">{getGatewayIcon(gateway.id)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">
                    {gateway.name}
                  </h3>
                  {gateway.priority === 1 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Primary
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  {getGatewayDescription(gateway)}
                </p>
              </div>
              <div className="flex-shrink-0">
                <input
                  type="radio"
                  name="payment-gateway"
                  value={gateway.id}
                  checked={selectedGateway === gateway.id}
                  onChange={() => !disabled && onGatewaySelect(gateway.id)}
                  disabled={disabled}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {gateways.length > 1 && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> You can choose between different payment gateways. 
            The primary gateway is recommended for the best experience.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentGatewaySelector;